import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/common/ui/card';
import { Button } from '@/common/ui/button';
import { Input } from '@/common/ui/input';
import { Label } from '@/common/ui/label';
import { Textarea } from '@/common/ui/textarea';
import { Checkbox } from '@/common/ui/checkbox';
import { Badge } from '@/common/ui/badge';
import {
  useGetTeamBioQuery,
  useSaveTeamCommitmentMutation,
} from '../../../api/teamCommitmentApi';
import { getSelectedAssessmentPeriod } from '@/common/selectors/auth';
import { useCultureHubQueryParams } from '../../../hooks/useCultureHubQueryParams';
import { ASSESSMENT_PERIOD_CLOSED } from 'areas/assessment-schedule/constants';
import { Users, Target, Loader2 } from 'lucide-react';

import { getMetricTypeDisplay } from '@/areas/settings/performance-metrics/components/MetricTypeSelector';

interface TeamBioProps {
  // Props removed in favor of hooks/selectors
}

export const TeamBio: React.FC<TeamBioProps> = () => {
  const { assessmentPeriodId, teamId } = useCultureHubQueryParams();
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const isEditable = Boolean(selectedAssessmentPeriod && selectedAssessmentPeriod.status !== ASSESSMENT_PERIOD_CLOSED);

  const { data: teamBioData, isLoading } = useGetTeamBioQuery(
    {
      teamId: teamId ?? 0,
      assessmentPeriodId: assessmentPeriodId ?? 0,
    },
    { skip: !assessmentPeriodId || !teamId }
  );

  const [saveCommitment, { isLoading: isSaving }] = useSaveTeamCommitmentMutation();

  const [selectedMetrics, setSelectedMetrics] = useState<number[]>([]);
  const [metricBenchmarks, setMetricBenchmarks] = useState<Record<string, number>>({});
  const [successCriteriaNotes, setSuccessCriteriaNotes] = useState('');
  const [primaryMetricId, setPrimaryMetricId] = useState<number | null>(null);

  const teamName = teamBioData?.team?.name || '';
  const commitment = teamBioData?.commitment || null;
  const previousPeriodCommitment = teamBioData?.previous_period_commitment || null;
  const previousBenchmarkMap: Record<string, number> = previousPeriodCommitment?.metric_benchmarks || {};
  const previousSuccessCriteriaNotes: string = previousPeriodCommitment?.success_criteria_notes || '';
  const availableMetrics = useMemo(() => teamBioData?.available_metrics || [], [teamBioData?.available_metrics]);
  const previousSelectedMetricNames = useMemo(() => {
    const prevIds: number[] = previousPeriodCommitment?.selected_metrics || [];
    return prevIds.map(id => availableMetrics.find(m => m.id === id)?.metric_name || id).filter(Boolean);
  }, [previousPeriodCommitment?.selected_metrics, availableMetrics]);

  // Partition metrics into those that have data (current/target/previous) and those without data
  const confirmMetrics = useMemo(() => availableMetrics.filter(m => {
      const hasPrevious = (m.previous_value !== null && m.previous_value !== undefined) || previousBenchmarkMap[m.id.toString()] !== undefined;
      const hasTarget = m.target_value !== null && m.target_value !== undefined && m.target_value !== 0;
      const hasCurrentValue = m.value !== null && m.value !== undefined && m.value !== 0;
      return hasPrevious || hasTarget || hasCurrentValue;
    }), [availableMetrics, previousBenchmarkMap]);

  const naMetrics = useMemo(() => availableMetrics.filter(m => !confirmMetrics.includes(m)), [availableMetrics, confirmMetrics]);

  const displayMetricValue = (metric: any) => metric.value !== 0 && metric.value ? metric.value : 'N/A';

  const teamMembers = teamBioData?.team_members || [];
  const leader = teamBioData?.leader ?? null;

  // Members no longer have start/end dates — assume they're active for the
  // purposes of the team bio/assessment period. Return the full list directly.
  const visibleTeamMembers = useMemo(() => teamMembers || [], [teamMembers]);

  // Leaders likewise are always considered present; don't filter by dates.
  const leaderInPeriod = useMemo(() => leader ?? null, [leader]);

  // Reset form state when selected assessment period or team changes so we represent the data for the selected period
  useEffect(() => {
    setSelectedMetrics([]);
    setMetricBenchmarks({});
    setSuccessCriteriaNotes('');
    setPrimaryMetricId(null);
  }, [assessmentPeriodId, teamId]);

  useEffect(() => {
    if (!teamBioData) return;

    const currentCommitment = teamBioData.commitment;
    const prevCommitment = teamBioData.previous_period_commitment;
    const metrics = teamBioData.available_metrics || [];

    if (currentCommitment?.selected_metrics) {
      setSelectedMetrics([...currentCommitment.selected_metrics]);
      setMetricBenchmarks({ ...(currentCommitment.metric_benchmarks || {}) });
      setSuccessCriteriaNotes(currentCommitment.success_criteria_notes || '');
      if (currentCommitment.selected_metrics.length > 0) {
        setPrimaryMetricId(currentCommitment.selected_metrics[0]);
      }
      return;
    }

    if (metrics.length > 0) {
      const allMetricIds = metrics.map(m => m.id);
      setSelectedMetrics(allMetricIds);

      if (prevCommitment?.selected_metrics) {
        // Pre-select previous selected metrics if available
        const prevSelected = prevCommitment.selected_metrics || [];
        // Copy any benchmarks that exist for the metrics
        const previousBenchmarks = prevCommitment?.metric_benchmarks || {};
        const benchmarksToCopy: Record<string, number> = {};
        allMetricIds.forEach(metricId => {
          const benchmarkValue = previousBenchmarks[metricId.toString()];
          if (benchmarkValue !== undefined) {
            benchmarksToCopy[metricId.toString()] = benchmarkValue;
          }
        });
        setMetricBenchmarks(benchmarksToCopy);

        const filteredPrevSelected = prevSelected.filter(id => allMetricIds.includes(id));
        setSelectedMetrics(filteredPrevSelected.length > 0 ? filteredPrevSelected : allMetricIds);

        if (filteredPrevSelected.length > 0 && allMetricIds.includes(filteredPrevSelected[0])) {
          setPrimaryMetricId(filteredPrevSelected[0]);
        } else {
          setPrimaryMetricId(allMetricIds[0]);
        }
      } else {
        setPrimaryMetricId(allMetricIds[0]);
      }
    }
  }, [teamBioData]);

  const handleMetricToggle = (metricId: number) => {
    if (!isEditable) return;
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        const newSelected = prev.filter(id => id !== metricId);
        const newBenchmarks = { ...metricBenchmarks };
        delete newBenchmarks[metricId.toString()];
        setMetricBenchmarks(newBenchmarks);
        if (primaryMetricId === metricId) {
          setPrimaryMetricId(newSelected.length > 0 ? newSelected[0] : null);
        }
        return newSelected;
      } else {
        const newSelected = [...prev, metricId];
        if (!primaryMetricId) {
          setPrimaryMetricId(metricId);
        }
        return newSelected;
      }
    });
  };

  const handlePrimaryMetricChange = (metricId: number) => {
    if (!isEditable) return;
    setPrimaryMetricId(metricId);
  };

  const handleBenchmarkChange = (metricId: number, value: string) => {
    if (!isEditable) return;
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setMetricBenchmarks(prev => ({
        ...prev,
        [metricId.toString()]: numValue,
      }));
    }
  };

  const handleSave = async () => {
    if (!assessmentPeriodId || !teamId) return;

    try {
      const metricsToSave = [...selectedMetrics];
      if (primaryMetricId && metricsToSave.includes(primaryMetricId)) {
        const primaryIndex = metricsToSave.indexOf(primaryMetricId);
        if (primaryIndex > 0) {
          metricsToSave.splice(primaryIndex, 1);
          metricsToSave.unshift(primaryMetricId);
        }
      }

      if (!teamId || !assessmentPeriodId) {
        throw new Error('Team ID and Assessment Period ID are required');
      }
      await saveCommitment({
        team_id: teamId,
        assessment_period_id: assessmentPeriodId,
        selected_metrics: metricsToSave,
        metric_benchmarks: metricBenchmarks,
        success_criteria_notes: successCriteriaNotes,
      }).unwrap();
    } catch (error) {
      console.error('Failed to save team commitment:', error);
    }
  };


  const isConfirmed = Boolean(commitment);

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      minHeight: 0,
    }}>
      <Card className="rounded-2xl border-gray-200 shadow-lg" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        backgroundColor: 'white',
      }}>
        <CardHeader
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200"
          style={{ flexShrink: 0, padding: 0 }}
        >
          <div
            className="flex items-start justify-between gap-4"
            style={{
              padding: '24px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div className="flex-grow">
              <CardTitle className="text-2xl text-gray-900 m-0 flex items-center mb-3" style={{
                lineHeight: '1.2',
                margin: 0,
                gap: 4,
              }}>
                <Users className="w-6 h-6 text-blue-600" />
                {teamName ? `Team Bio - ${teamName}` : 'Team Bio'}
              </CardTitle>
              <CardDescription className="text-description" style={{ marginLeft: '52px', marginTop: '0' }}>
                Define your team's goals and success criteria for this assessment period
              </CardDescription>
            </div>
            <div className="flex-shrink">
              {!isEditable && (
                <Badge variant="destructive" className="text-xs">Read-only</Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6" style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'auto',
          paddingBottom: '64px',
        }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Team Members Section */}
              <div className="space-relaxed">
                <h3 className="heading-lg flex items-center" style={{ gap: 4 }}>
                  <Users className="icon-md" />
                  Team Members
                </h3>
                <div className="rounded-lg p-4 border border-gray-200">
                  {/* Helper: simple avatar with initials */}
                  {leaderInPeriod ? (
                    <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-medium`} style={{ backgroundColor: (() => {
                          const colors = ['#0ea5e9','#7c3aed','#ef4444','#f59e0b','#10b981','#6366f1'];
                          const idx = Math.abs(leaderInPeriod.id) % colors.length;
                          return colors[idx];
                        })() }}>
                          {`${(leaderInPeriod.firstName || '').charAt(0)}${(leaderInPeriod.lastName || '').charAt(0)}`.toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Team leader: {leaderInPeriod.firstName} {leaderInPeriod.lastName}</div>
                          {leaderInPeriod.email && <div className="text-secondary">{leaderInPeriod.email}</div>}
                        </div>
                      </div>
                      <div className="flex-shrink">
                        <Badge variant="secondary" className="text-xs">Leader</Badge>
                      </div>
                    </div>
                  ) : null}

                  {visibleTeamMembers.length > 0 ? (
                    <>
                      {teamMembers.length !== visibleTeamMembers.length && (
                        <div className="text-xs text-gray-500 italic mb-2">Showing {visibleTeamMembers.length} of {teamMembers.length} members active during this period</div>
                      )}
                      <div className="divide-y divide-gray-100">
                        {visibleTeamMembers.map((member, idx) => (
                          <div key={idx} className="flex items-center gap-3 py-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-medium`} style={{ backgroundColor: (() => {
                              const colors = ['#60a5fa','#a78bfa','#fb7185','#fbbf24','#34d399','#818cf8'];
                              const idx = Math.abs(member.id) % colors.length;
                              return colors[idx];
                            })() }}>
                              {`${(member.firstName || '').charAt(0)}${(member.lastName || '').charAt(0)}`.toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm text-gray-900">{(member.firstName || member.lastName) ? `${member.lastName}${member.firstName ? `, ${member.firstName}` : ''}` : ''}</div>
                              {member.email && <div className="text-secondary">{member.email}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No team members found</p>
                  )}
                </div>
              </div>

              {/* Metrics Section - Single unified list */}
              <div className="space-relaxed">
                <div className="flex-center-between">
                  <div>
                    <h3 className="heading-lg flex items-center" style={{ gap: 4 }}>
                      <Target className="icon-md" />
                      {selectedMetrics.length > 0 ? 'Confirm the existing metrics' : 'Add some metrics'}
                    </h3>
                    {!commitment && previousPeriodCommitment && selectedMetrics.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Metrics from the previous assessment period have been pre-selected for your confirmation
                      </p>
                    )}
                    {!commitment && previousPeriodCommitment?.selected_metrics && previousPeriodCommitment.selected_metrics.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">Previous selection: {previousSelectedMetricNames.join(', ') || 'N/A'}</p>
                    )}
                  </div>
                  {selectedMetrics.length > 0 && !primaryMetricId && (
                    <p className="text-sm text-red-500">
                      Please select a primary metric <span className="text-red-500">*</span>
                    </p>
                  )}
                </div>

                <div className="space-y-2 border border-gray-200 rounded-lg p-4">
                  {availableMetrics.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No metrics available for this team
                    </p>
                  ) : (
                    <>
                      {/* Confirm section: metrics with data */}
                      {confirmMetrics.length > 0 ? (
                        confirmMetrics.map((metric) => {
                          const isSelected = selectedMetrics.includes(metric.id);
                          const isPrimary = primaryMetricId === metric.id;
                          return (
                            <div key={metric.id} className={`p-4 rounded-lg border ${isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}>
                              <div className="flex items-start gap-3">
                                <Checkbox id={`metric-${metric.id}`} checked={isSelected} onCheckedChange={() => handleMetricToggle(metric.id)} className="mt-1" disabled={!isEditable} />
                                <div className="flex-1 space-y-3">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-grow">
                                      <Label htmlFor={`metric-${metric.id}`} className="text-sm font-medium text-gray-900 cursor-pointer block">
                                        {metric.metric_name}
                                      </Label>
                                      <span className="text-secondary">{getMetricTypeDisplay(metric.metric_type)}</span>
                                    </div>
                                    {isSelected && (
                                      <div className="flex-center-gap">
                                        <input type="radio" id={`primary-${metric.id}`} name="primary-metric" checked={isPrimary} onChange={() => handlePrimaryMetricChange(metric.id)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" disabled={!isEditable} />
                                        <Label htmlFor={`primary-${metric.id}`} className="text-xs text-gray-600 cursor-pointer">Primary</Label>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <span>Current: {displayMetricValue(metric)}</span>
                                    {metric.target_value && <span>Target: {metric.target_value}</span>}
                                    {!commitment && (
                                      <span className="ml-2">Previous: {metric.previous_value !== null && metric.previous_value !== undefined ? metric.previous_value : (previousBenchmarkMap[metric.id.toString()] !== undefined ? previousBenchmarkMap[metric.id.toString()] : 'N/A')}</span>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <div className="mt-2">
                                      <Label htmlFor={`benchmark-${metric.id}`} className="text-xs text-gray-600 mb-1 block">Benchmark Target</Label>
                                      <Input id={`benchmark-${metric.id}`} type="number" step="0.01" value={metricBenchmarks[metric.id.toString()]?.toString() || metric.target_value?.toString() || ''} onChange={(e) => handleBenchmarkChange(metric.id, e.target.value)} placeholder="Enter target value" className="full-width" disabled={!isEditable} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">No metrics to confirm</p>
                      )}

                      {/* N/A section: metrics without data */}
                      {naMetrics.length > 0 && (
                        <>
                          <hr className="my-3 border-t border-gray-100" />
                          <h4 className="heading-sm">Metrics with no data (N/A)</h4>
                          <p className="text-xs text-gray-500 mb-2">These metrics have no current or previous values and are shown as N/A</p>
                          {naMetrics.map((metric) => {
                            const isSelected = selectedMetrics.includes(metric.id);
                            return (
                              <div key={metric.id} className={`p-4 rounded-lg border ${isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}>
                                <div className="flex items-start gap-3">
                                  <Checkbox id={`metric-${metric.id}`} checked={isSelected} onCheckedChange={() => handleMetricToggle(metric.id)} className="mt-1" disabled={!isEditable} />
                                  <div className="flex-1 space-y-3">
                                    <div className="flex items-center gap-3">
                                      <div className="flex-grow">
                                        <Label htmlFor={`metric-${metric.id}`} className="text-sm font-medium text-gray-900 cursor-pointer block">{metric.metric_name}</Label>
                                        <span className="text-secondary">{getMetricTypeDisplay(metric.metric_type)}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <span>Current: N/A</span>
                                      {metric.target_value && <span>Target: {metric.target_value}</span>}
                                      {!commitment && (
                                        <span className="ml-2">Previous: {metric.previous_value !== null && metric.previous_value !== undefined ? metric.previous_value : (previousBenchmarkMap[metric.id.toString()] !== undefined ? previousBenchmarkMap[metric.id.toString()] : 'N/A')}</span>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <div className="mt-2">
                                        <Label htmlFor={`benchmark-${metric.id}`} className="text-xs text-gray-600 mb-1 block">Benchmark Target</Label>
                                        <Input id={`benchmark-${metric.id}`} type="number" step="0.01" value={metricBenchmarks[metric.id.toString()]?.toString() || metric.target_value?.toString() || ''} onChange={(e) => handleBenchmarkChange(metric.id, e.target.value)} placeholder="Enter target value" className="full-width" disabled={!isEditable} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Success Criteria */}
              <div className="space-relaxed" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <h3 className="text-title-section">What does success look like?</h3>
                <div className="space-normal" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <Textarea
                    id="success-criteria"
                    value={successCriteriaNotes}
                    onChange={(e) => isEditable && setSuccessCriteriaNotes(e.target.value)}
                    placeholder="Describe what success looks like for your team this assessment period..."
                    className="min-h-[120px]"
                    style={{ flex: 1, minHeight: '120px' }}
                    readOnly={!isEditable}
                  />
                  {!commitment && (
                    <div className="text-xs text-gray-500 italic mt-2">Previous: {previousSuccessCriteriaNotes || 'N/A'}</div>
                  )}
                </div>
              </div>

              <div style={{ height: 96, flexShrink: 0 }} />

            </>
          )}
        </CardContent>

{isEditable && (
        <CardFooter
          className="bg-gray-50 border-t border-gray-200"
          style={{ flexShrink: 0, padding: 0 }}
        >
          <div
            className="flex justify-end"
            style={{
              padding: '20px 24px',
              width: '100%',
              boxSizing: 'border-box',
              gap: '4px',
              display: 'flex',
            }}
          >
            <Button
            variant="outline"
            onClick={() => {
              if (commitment) {
                setSelectedMetrics(commitment.selected_metrics || []);
                setMetricBenchmarks(commitment.metric_benchmarks || {});
                setSuccessCriteriaNotes(commitment.success_criteria_notes || '');
                if (commitment.selected_metrics && commitment.selected_metrics.length > 0) {
                  setPrimaryMetricId(commitment.selected_metrics[0]);
                } else {
                  setPrimaryMetricId(null);
                }
              } else if (previousPeriodCommitment) {
                const previousMetrics = previousPeriodCommitment.selected_metrics || [];
                const availableMetricIds = new Set(availableMetrics.map(m => m.id));
                const metricsToSelect = previousMetrics.filter(id => availableMetricIds.has(id));

                setSelectedMetrics(metricsToSelect);

                const previousBenchmarks = previousPeriodCommitment.metric_benchmarks || {};
                const benchmarksToCopy: Record<string, number> = {};
                metricsToSelect.forEach(metricId => {
                  const benchmarkValue = previousBenchmarks[metricId.toString()];
                  if (benchmarkValue !== undefined) {
                    benchmarksToCopy[metricId.toString()] = benchmarkValue;
                  }
                });
                setMetricBenchmarks(benchmarksToCopy);
                setSuccessCriteriaNotes('');

                if (previousMetrics.length > 0) {
                  const previousPrimaryMetricId = previousMetrics[0];
                  if (availableMetricIds.has(previousPrimaryMetricId)) {
                    setPrimaryMetricId(previousPrimaryMetricId);
                  } else if (metricsToSelect.length > 0) {
                    setPrimaryMetricId(metricsToSelect[0]);
                  } else {
                    setPrimaryMetricId(null);
                  }
                } else {
                  setPrimaryMetricId(null);
                }
              } else {
                setSelectedMetrics([]);
                setMetricBenchmarks({});
                setSuccessCriteriaNotes('');
                setPrimaryMetricId(null);
              }
            }}
            disabled={isSaving || isLoading || !isEditable}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading || selectedMetrics.length === 0 || !primaryMetricId || !isEditable}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Team Bio'
            )}
          </Button>
          </div>
        </CardFooter>
)}
      </Card>
    </div>
  );
};

